/**
 * ComplianceGate — higher-order guard that sits inside ProtectedRoute.
 *
 * If the authenticated user is missing any required consent (per
 * /api/consent/me), they are redirected to /onboarding/consent before they
 * can access the wrapped page. The original target is preserved in
 * location.state so we can bounce them back after completion.
 *
 * Intentionally bypassed for a handful of "safe" routes the user must be
 * able to reach even mid-compliance (legal docs themselves, logout flow,
 * privacy settings, and the gate itself).
 */
import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const BYPASS_PREFIXES = [
    "/onboarding/consent",
    "/legal",
    "/settings/privacy",
];

export default function ComplianceGate({ children }) {
    const { user } = useAuth();
    const location = useLocation();
    const [state, setState] = useState({ checking: true, compliant: true });

    const bypass = BYPASS_PREFIXES.some((p) => location.pathname.startsWith(p));

    useEffect(() => {
        if (!user || bypass) {
            setState({ checking: false, compliant: true });
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/consent/me");
                if (!cancelled) {
                    setState({ checking: false, compliant: !!data?.compliant });
                }
            } catch (_e) {
                // On error, don't trap the user — allow access; the server
                // still enforces per-endpoint where needed.
                if (!cancelled) setState({ checking: false, compliant: true });
            }
        })();
        return () => { cancelled = true; };
    }, [user, location.pathname, bypass]);

    if (state.checking) return null;
    if (!state.compliant) {
        return (
            <Navigate
                to="/onboarding/consent"
                replace
                state={{ next: location.pathname + location.search }}
            />
        );
    }
    return children;
}
